<template>
  <div class="event-modal-documents">
    <header class="event-modal-documents-header">
      <h1 class="event-modal-documents-header__title">
        {{ $t("event.documents.titleLarge") }}
      </h1>
      <div class="documents-info" v-if="isServiceShown">
        <icon name="warning-border--no-color" />
        <div class="documents-info__text">
          {{ $t("event.documents.invoiceLocation.part1") }}
          <router-link :to="{ name: 'orders', params: { id: event.id } }">
            {{ $t("event.documents.invoiceLocation.part2") }}
          </router-link>
        </div>
      </div>
    </header>

    <div class="event-modal-documents__content">
      <div class="event-modal-documents-navigation">
        <ul class="event-modal-documents-navigation__list">
          <li
            v-for="item in listTabs"
            :key="`nav-${item.id}`"
            class="event-modal-documents-navigation__list-item"
            :class="[
              {
                _active: item.id === currTab
              },
              {
                _highlight: item.id === currTab
              }
            ]"
          >
            <button
              class="event-modal-documents-navigation__list-item-action"
              @click="currTab = item.id"
            >
              {{ item.title }}
            </button>
          </li>
        </ul>
      </div>

      <div class="event-modal-documents__container">
        <component v-if="currTab" :is="currComponent" />
      </div>
    </div>
  </div>
</template>

<script>
import EventModalDocumentsContentActs from "@/components/Modal/Documents/EventModalDocumentsContentActs.vue";
import EventModalDocumentsContentIssuedInvoices from "@/components/Modal/Documents/EventModalDocumentsContentIssuedInvoices.vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "EventDocumentsPopup",
  components: {
    EventModalDocumentsContentIssuedInvoices,
    EventModalDocumentsContentActs
  },
  props: {
    tab: {
      type: String,
      default: "issuedInvoices"
    }
  },
  data() {
    return {
      currTab: null,
      listTabs: [
        {
          id: "issuedInvoices",
          nameContentComponent: "EventModalDocumentsContentIssuedInvoices",
          title: this.$t("event.documents.issuedInvoices")
        },
        {
          id: "acts",
          nameContentComponent: "EventModalDocumentsContentActs",
          title: this.$t("event.documents.acts")
        }
      ]
    };
  },
  computed: {
    ...mapState("event", ["event"]),
    ...mapGetters("event", ["isServiceShown"]),
    currComponent() {
      const tab = this.listTabs.find(el => el.id === this.currTab);
      return tab.nameContentComponent;
    }
  },
  mounted() {
    this.currTab = this.tab;
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";
.documents-info {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: 700px;
  margin-top: 30px;
  box-sizing: border-box;

  & a {
    color: inherit;
  }

  .svg-icon {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    color: #cdced0;

    @include respond-down(md) {
      width: 16px;
      height: 16px;
    }
  }
  &__text {
    flex: 1;
    font-size: 13px;
    line-height: 20px;
    color: #000000;

    @include respond-down(md) {
      font-size: 9px;
      line-height: 12px;
    }
  }
}

.event-modal-documents {
  min-height: 100vh;
  background: rgba(#f9f9f9, 0.5);

  &__content {
    padding-top: 38px;
    border-top: 2px solid #f1f1f1;

    @include respond-down(lg) {
      padding-top: 12px;
    }
    @include respond-down(md) {
      padding-top: 3px;
    }
  }
  &__container {
    padding: 40px 0 40px 80px;

    width: 780px;

    margin-bottom: auto;

    @include respond-down(lg) {
      padding: 40px 24px 24px 24px;
      width: 100%;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      padding: 22px 16px 16px 16px;
    }
  }
}

.event-modal-documents-header {
  padding: 56px 0 56px 80px;
  background: #fff;

  @include respond-down(lg) {
    padding: 40px 24px 35px;
    box-sizing: border-box;
  }
  @include respond-down(md) {
    padding: 27px 16px 24px;
  }
  &__title {
    @include h1();
    width: 780px;

    @include respond-down(lg) {
      width: 100%;
      font-size: 24px;
    }
    @include respond-down(md) {
      font-size: 18px;
    }
  }
}

.event-modal-documents-navigation {
  &__list {
    list-style: none;
    display: flex;
    width: 780px;
    margin: 0 0 0 80px;
    padding: 0;
    border-bottom: 2px solid #f1f1f1;

    @include respond-down(lg) {
      width: calc(100% - 24 * 2px);
      margin: 0 24px;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      width: calc(100% - 16 * 2px);
      margin: 0 16px -3px;

      flex-wrap: nowrap;
      overflow-x: scroll;

      // to see box-shadow-ish border
      padding-bottom: 2px;

      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: -ms-autohiding-scrollbar;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &-item {
      margin-right: 68px;

      @include respond-down(md) {
        margin-right: 28px;
        white-space: nowrap;
      }
      &:last-child {
        margin-right: 0;
      }
      &-action {
        @include button-clean();
        padding: 15px 0;
        font-size: 13px;
        line-height: 1.54;
        color: #000000;
        user-select: none;
        cursor: pointer;
      }

      &._highlight {
        box-shadow: 0 2px #005197;
      }

      &._active &-action {
        color: #005197;
        font-weight: bold;
      }
    }
  }
}
</style>

<style lang="scss">
@import "@v2/app/styles/media";

.documents-table {
  &__wrap {
    width: 100%;
  }
  &__header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: center;

    width: 100%;
    min-height: 55px;
    margin-bottom: 20px;

    font-weight: bold;
    font-size: 15px;
    line-height: 21px;

    background: #005197;
    color: #ffffff;

    @include respond-down(lg) {
      margin-bottom: 10px;
      font-size: 12px;
      line-height: 21px;
    }
    @include respond-down(md) {
      display: none;
    }
  }
  &__row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: center;
    min-height: 70px;

    font-size: 14px;
    line-height: 21px;
    color: #000000;

    @include respond-down(lg) {
      font-size: 12px;
    }
    @include respond-down(md) {
      flex-wrap: wrap;
      justify-content: space-between;
      min-height: auto;
      padding: 17px 14px 7px;
      font-size: 10px;
    }
    .documents-table__col {
      padding-top: 10px;
      padding-bottom: 10px;

      @include respond-down(md) {
        padding-top: 0;
        padding-bottom: 6px;
      }
    }
  }
  &__col {
    padding-left: 5px;
    padding-right: 5px;
    box-sizing: border-box;
    text-align: center;
  }
  &__title-mobile {
    display: none;

    @include respond-down(md) {
      display: block;
      margin-bottom: 7px;
      font-weight: bold;
      font-size: 10px;
      line-height: 12px;
      text-align: center;
      color: #005197;
    }
  }
  &__icon-download {
    display: inline-block;
    vertical-align: middle;
    color: #009ee3;
    cursor: pointer;

    @include respond-down(md) {
      margin-bottom: 6px;
    }
    .svg-icon {
      display: inline-block;
      vertical-align: middle;
      width: 16px;
      max-height: 16px;
      height: auto;
    }
  }
}

.item-documents-table {
  margin-bottom: 20px;
  border: 1px solid #dcdcdc;

  @include respond-down(lg) {
    margin-bottom: 10px;
  }
  @include respond-down(md) {
    margin-bottom: 19px;
    border: none;
  }
  &__footer {
    padding: 14px 0;
    border-top: 1px solid #dcdcdc;

    @include respond-down(lg) {
      padding: 4px 0 8px;
    }
    @include respond-down(md) {
      padding-top: 8px;
    }
    .item-footer {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-content: flex-start;
      align-items: center;

      margin-bottom: 20px;
      font-size: 13px;
      line-height: 20px;
      color: #7a7a7a;

      @include respond-down(lg) {
        font-size: 12px;
      }
      @include respond-down(md) {
        margin-bottom: 16px;
        font-size: 10px;
        line-height: 13px;
      }
      &:after {
        display: block;
        content: "";
        position: absolute;
        bottom: -10px;
        left: 26px;
        right: 26px;
        height: 1px;
        background-color: rgba(220, 220, 220, 0.3);

        @include respond-down(md) {
          bottom: -8px;
        }
      }
      &:last-child {
        &:after {
          display: none;
        }
      }
      &__col {
        &--info {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          justify-content: flex-start;
          align-content: flex-start;
          align-items: center;
          padding-left: 22px;
          box-sizing: border-box;

          @include respond-down(lg) {
            padding-left: 34px;
          }
          @include respond-down(md) {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: flex-start;
            align-content: flex-start;
            align-items: center;
            padding-left: 9px;
          }
          .title {
            margin-right: 10px;

            @include respond-down(md) {
              flex: 1;
              margin-right: 10px;
            }
          }
          .count {
            padding: 0 5px;
          }
        }
        &--sum {
          text-align: center;
        }
      }
    }
  }
  &__footer-list {
    margin-top: 8px;

    @include respond-down(lg) {
      margin-top: 17px;
    }
    @include respond-down(md) {
      margin-top: 14px;
    }
  }
  &__footer-btn {
    display: inline-block;
    vertical-align: top;
    margin: 0 22px;
    font-size: 13px;
    line-height: 20px;
    color: #009ee3;
    cursor: pointer;

    @include respond-down(lg) {
      margin-top: 5px;
      margin-left: 33px;
      font-size: 12px;
    }
    @include respond-down(md) {
      margin: 0 9px 0;
      font-size: 10px;
    }
    span {
      display: inline-block;
      vertical-align: middle;

      &.close {
        margin-bottom: 11px;

        @include respond-down(md) {
          margin-bottom: 0;
        }
      }
    }
    .svg-icon {
      display: inline-block;
      vertical-align: middle;
      width: 8px;
      height: 8px;
      margin-left: 8px;

      @include respond-down(lg) {
        margin-left: 11px;
      }
      &.top {
        margin-bottom: 12px;
        transform: rotate(180deg);

        @include respond-down(md) {
          margin-bottom: 0;
        }
      }
    }
  }
}

.event-modal-documents__container .documents-table__wrap {
  .ps__rail-y {
    display: none;
  }
  .ps--active-x .ps__rail-x {
    @media (max-width: 739px) {
      opacity: 1;
    }
  }
  .ps__rail-x,
  .ps__rail-x:hover,
  .ps__rail-x.ps--clicking {
    @media (max-width: 739px) {
      opacity: 1;
      height: 3px;
      background-color: rgba(0, 81, 151, 0.2);
    }
    @media (min-width: 740px) {
      display: none !important;
    }
  }
  .ps__thumb-x,
  .ps__rail-x:hover .ps__thumb-x,
  .ps__rail-x.ps--clicking .ps__thumb-x {
    @media (max-width: 739px) {
      bottom: 0;
      height: 3px;
      background-color: #005197;
    }
  }
  &.ps--active-x .ps__rail-x {
    @media (max-width: 739px) {
      opacity: 1;
    }
  }
}
</style>
